import './bootstrap';
import 'primeicons/primeicons.css';
import '../css/app.css';

import * as Sentry from '@sentry/vue';
import { createApp, h, watch } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from '../../vendor/tightenco/ziggy';
import { usePage } from '@inertiajs/vue3';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import { autoAnimatePlugin } from '@formkit/auto-animate/vue';
import Toast from 'primevue/toast';
import DialogService from 'primevue/dialogservice';
import ConfirmationService from 'primevue/confirmationservice';
import Ripple from 'primevue/ripple';
import Tooltip from 'primevue/tooltip';
import { usePreset } from '@primevue/themes';

import StyleClass from 'primevue/styleclass';

const appName = import.meta.env.VITE_APP_NAME || 'MHA';

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_LARAVEL_DSN,
            release: import.meta.env.VITE_SENTRY_RELEASE,
            environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'local',
            integrations: [
                Sentry.httpClientIntegration(),
                Sentry.browserTracingIntegration(),
                Sentry.browserProfilingIntegration(),
            ],
            tracesSampleRate: 1.0,
            tracePropagationTargets: ['localhost', /^http:\/\/agency\.test/],
            profilesSampleRate: 1.0,
        });

        app.config.globalProperties.theme = {
            get isDark() {
                const prefersDarkScheme = window.matchMedia('(prefers-color-scheme: dark)');
                const storedTheme = Boolean(localStorage.getItem('theme'));
                return !storedTheme;
            },
        };

        const loadTheme = (platformCode) => {
            const code = (platformCode || 'fc').toLowerCase();
            return import(`./themes/${code}.js`)
                .then((themeModule) => themeModule.default)
                .catch((error) => {
                    console.warn(`Theme file for platform "${code}" not found, falling back to FC theme`);
                    return import('./themes/fc.js').then((fcThemeModule) => fcThemeModule.default);
                });
        };

        const initialPlatformCode = props.initialPage.props.currentPlatform?.code || 'fc';
        loadTheme(initialPlatformCode).then((initialTheme) => {
            app.use(plugin)
                .use(autoAnimatePlugin)
                .use(ZiggyVue)
                .use(PrimeVue, {
                    theme: {
                        preset: initialTheme,
                        options: {
                            prefix: 'p',
                            darkModeSelector: '.dark',
                            cssLayer: {
                                name: 'primevue',
                                order: 'tailwind-base, primevue, tailwind-utilities',
                            },
                        },
                    },
                    ripple: true,
                })
                .use(ToastService)
                .use(DialogService)
                .use(ConfirmationService)
                .component('Toast', Toast)
                .directive('ripple', Ripple)
                .directive('tooltip', Tooltip)
                .directive('styleclass', StyleClass)
                .mount(el);
        });

        // Watch for platform changes and update theme reactively
        const page = usePage();
        watch(
            () => page.props?.currentPlatform?.code,
            (newPlatformCode, oldPlatformCode) => {
                if (newPlatformCode && newPlatformCode !== oldPlatformCode) {
                    loadTheme(newPlatformCode).then((newTheme) => {
                        usePreset(newTheme);
                    });
                }
            },
            { immediate: false },
        );
    },
    progress: {
        color: '{primary.500}',
        delay: 250,
        showSpinner: false,
    },
});

console.log(
    '\n%cLooking for support?\n%cPlease visit: https://creatorhelp.fancentro.com/hc/en-us/',
    'color:#8878B5;font-size:1.2em;',
    'color:#aaa;font-size:0.8em;',
);
